// Central utility for binding period normalization and display
// This ensures consistency across all rate tables and components

export type BindingPeriodKey = 
  | '3_months' 
  | '1_year' 
  | '2_years' 
  | '3_years' 
  | '4_years' 
  | '5_years' 
  | '6_years'
  | '7_years' 
  | '8_years' 
  | '9_years'
  | '10_years';

// Canonical display order for all tables
export const DISPLAY_ORDER: BindingPeriodKey[] = [
  '3_months',
  '1_year',
  '2_years',
  '3_years',
  '4_years',
  '5_years',
  '7_years',
  '8_years',
  '10_years'
];

// Display labels - consistent format across all tables
export const PERIOD_LABELS: Record<BindingPeriodKey, string> = {
  '3_months': '3 mån',
  '1_year': '1 år',
  '2_years': '2 år',
  '3_years': '3 år',
  '4_years': '4 år',
  '5_years': '5 år',
  '6_years': '6 år',
  '7_years': '7 år',
  '8_years': '8 år',
  '9_years': '9 år',
  '10_years': '10 år'
};

// Normalization map: Swedish labels and variations -> canonical keys
const NORMALIZATION_MAP: Record<string, BindingPeriodKey> = {
  // Swedish labels (long format)
  '3 månader': '3_months',
  'rörlig': '3_months',
  // Swedish labels (short format from låneguiden.se)
  '3 mån': '3_months',
  // Standard Swedish format
  '1 år': '1_year',
  '2 år': '2_years',
  '3 år': '3_years',
  '4 år': '4_years',
  '5 år': '5_years',
  '6 år': '6_years',
  '7 år': '7_years',
  '8 år': '8_years',
  '9 år': '9_years',
  '10 år': '10_years',
  // Already canonical
  '3_months': '3_months',
  '1_year': '1_year',
  '2_years': '2_years',
  '3_years': '3_years',
  '4_years': '4_years',
  '5_years': '5_years',
  '6_years': '6_years',
  '7_years': '7_years',
  '8_years': '8_years',
  '9_years': '9_years',
  '10_years': '10_years'
};

/**
 * Converts any binding period string to its canonical key
 * @param period - Raw period string (e.g., "3 månader", "rörlig", "3_months")
 * @returns Canonical key or the original string if no mapping exists
 */
export function toCanonical(period: string): BindingPeriodKey | string {
  const normalized = NORMALIZATION_MAP[period.toLowerCase()];
  return normalized || period;
}

/**
 * Gets the display label for a canonical key
 * @param key - Canonical binding period key
 * @returns Display label (e.g., "3 mån", "1 år")
 */
export function toLabel(key: BindingPeriodKey | string): string {
  return PERIOD_LABELS[key as BindingPeriodKey] || key;
}

/**
 * Gets all periods in display order with their labels
 * @returns Array of { key, label } objects
 */
export function getDisplayPeriods() {
  return DISPLAY_ORDER.map(key => ({
    key,
    label: PERIOD_LABELS[key]
  }));
}
